import { Breadcrumb, BreadcrumbLink, BreadcrumbPage, Button } from '@ads/components-react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { createFileRoute, Link } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DashboardTable,
  DisciplineAutocomplete,
  useCorrectionDashboard,
} from '@/features/exam-correction';
import { DatePicker } from '@/shared/ui/atoms';
import { PageHeader } from '@/shared/ui/organisms/PageHeader';

export const Route = createFileRoute('/_authenticated/exam/correction-dashboard/')({
  component: CorrectionDashboardPage,
});

// ─── Página ───────────────────────────────────────────────────────────────────

function CorrectionDashboardPage() {
  const { t } = useTranslation('examCorrection');

  const defaultStartDate = dayjs().subtract(2, 'month').format('YYYY-MM-DD');
  const defaultEndDate = dayjs().add(1, 'month').format('YYYY-MM-DD');

  const [page, setPage] = useState(1);
  const [clearKey, setClearKey] = useState(0);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [disciplineId, setDisciplineId] = useState<number | undefined>(undefined);
  const [disciplineName, setDisciplineName] = useState('');

  const [appliedStartDate, setAppliedStartDate] = useState(defaultStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultEndDate);
  const [appliedDisciplineId, setAppliedDisciplineId] = useState<number | undefined>(undefined);

  const hasActiveFilters =
    Boolean(appliedStartDate) || Boolean(appliedEndDate) || appliedDisciplineId !== undefined;

  const { items, totalPages, totalCount, isLoading } = useCorrectionDashboard({
    page,
    ...(appliedStartDate ? { startDate: appliedStartDate } : {}),
    ...(appliedEndDate ? { endDate: appliedEndDate } : {}),
    ...(appliedDisciplineId !== undefined ? { disciplineId: appliedDisciplineId } : {}),
  });

  function handleApplyFilters(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPage(1);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedDisciplineId(disciplineId);
  }

  function handleClearFilters() {
    setStartDate('');
    setEndDate('');
    setDisciplineId(undefined);
    setDisciplineName('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setAppliedDisciplineId(undefined);
    setPage(1);
    setClearKey(k => k + 1);
  }

  return (
    <div className="flex flex-col gap-xxs">
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbLink asChild>
              <Link to="/">{t('correction.breadcrumb.home')}</Link>
            </BreadcrumbLink>
            <BreadcrumbPage>{t('correction.breadcrumb.dashboard')}</BreadcrumbPage>
          </Breadcrumb>
        }
      />

      {/* Filtros */}
      <form
        onSubmit={handleApplyFilters}
        className="grid grid-cols-1 gap-nano rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini sm:grid-cols-2 lg:grid-cols-3"
      >
        <DatePicker
          key={`start-${clearKey}`}
          label={t('dashboard.filters.startDate')}
          id="filter-start-date"
          value={startDate}
          onChange={setStartDate}
          size="md"
        />

        <DatePicker
          key={`end-${clearKey}`}
          label={t('dashboard.filters.endDate')}
          id="filter-end-date"
          size="md"
          value={endDate}
          onChange={setEndDate}
        />

        <DisciplineAutocomplete
          key={clearKey}
          label={t('dashboard.filters.discipline')}
          placeholder={t('dashboard.filters.disciplinePlaceholder')}
          value={disciplineId ?? null}
          selectedLabel={disciplineName}
          onChange={(id, name) => {
            setDisciplineId(id ?? undefined);
            setDisciplineName(name);
          }}
        />

        {/* Linha dos botões */}
        <div className="flex w-full items-end gap-nano">
          <Button size="md" type="submit" hierarchy="primary" leadingIcon={MagnifyingGlassIcon}>
            {t('dashboard.filters.apply')}
          </Button>

          {hasActiveFilters && (
            <Button
              size="md"
              type="button"
              hierarchy="secondary"
              leadingIcon={XMarkIcon}
              onClick={handleClearFilters}
            >
              {t('dashboard.filters.clear')}
            </Button>
          )}
        </div>
      </form>

      {/* Tabela */}
      <DashboardTable
        items={items}
        isLoading={isLoading}
        currentPage={page}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </div>
  );
}
